import type { StudyDocument, StudyDocumentAnalysisSummary } from './studyApi';

/**
 * Learner words for a material row in the Subject Materials tab and the Materials library: what
 * the material is, question progress, and a status only when the learner can act on it.
 * Internal status codes are never shown.
 *
 * Title rule (owner feedback 2026-10-08), used everywhere a material is named for the learner:
 * the learner-chosen name, else the analysed topic, else the file name.
 */

export interface MaterialHeading {
  title: string;
  /** The file name, shown small under the title. Null when the title is the file name. */
  subtitle: string | null;
}

export interface NameableMaterial {
  file_name: string;
  display_name?: string | null;
  analysis?: { topic?: string | null } | null;
}

/** The learner-facing name of a material: chosen name > analysed topic > file name. */
export function materialTitle(document: NameableMaterial): string {
  return document.display_name?.trim() || document.analysis?.topic?.trim() || document.file_name;
}

export function materialHeading(document: NameableMaterial): MaterialHeading {
  const title = materialTitle(document);
  return { title, subtitle: title === document.file_name ? null : document.file_name };
}

const STATUS_WORDS: Partial<Record<StudyDocument['status'], string>> = {
  needs_confirmation: 'Check the subject',
  processing: 'Getting ready…',
  failed: "Couldn't open this file",
};

/** Words for a status the learner should know about; null for `uploaded` / `ready`. */
export function materialStatusWords(status: StudyDocument['status']): string | null {
  return STATUS_WORDS[status] ?? null;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/** "2 questions · 1 done", "Analysing…", or null when there is nothing to show. */
export function materialProgressLine(
  analysis: StudyDocumentAnalysisSummary | null | undefined,
): string | null {
  if (!analysis) return null;
  if (analysis.status === 'queued' || analysis.status === 'running') return 'Analysing…';
  if (analysis.status === 'failed' || analysis.questions_total === 0) return null;
  return `${plural(analysis.questions_total, 'question')} · ${analysis.questions_done} done`;
}

/** The file name without its extension: "images.png" → "images". */
export function fileNameStem(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

/**
 * A filename-only "Possible topic" adds nothing when it is just the file name again
 * ("images.png" → "images"), so the row hides it.
 */
export function isFileNameTopic(topic: string, fileName: string): boolean {
  const normalise = (text: string) => text.trim().toLowerCase();
  return normalise(topic) === normalise(fileNameStem(fileName));
}
